import * as React from 'react';
import { useState, useRef, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { Combobox as BaseCombobox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useOnClickOutside } from 'usehooks-ts';
import { useTranslation } from 'next-i18next';

import { cn } from '@restworld/utils-common';
import { Spinner } from '@restworld/ui-components';
import {
  selectLabelStyle,
  selectSecondaryLabelStyle,
  SelectMainWrapper,
  selectInteractiveAreaStyle,
  ArrowIconWrapper,
  selectInteractiveAreaContentStyle,
  selectOptionsStyle,
  HelperTextVariantType,
  StyledSelectHelperText,
} from './combobox.styled';

export function Combobox({
  value,
  onChange,
  dropdownList,
  placeholder,
  inputWrapperStyle,
  listStyle,
  inputStyle,
  noOptionsMessage,
  error,
  isLoading,
  onQueryChange,
  disabled,
  label,
  secondaryLabel,
  fullwidth,
  autoCompleteOff = true,
  showIcon = true,
  noFilter = false,
  helperText: { variant: helperTextVariant, message: helperTextMessage } = { variant: 'error', message: '' },
}: ComboboxProps) {
  const { t: translateCommon } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const filteredOptions =
    query === '' || noFilter
      ? dropdownList
      : dropdownList?.filter((v: string) => v.toLowerCase().includes(query.toLowerCase()));

  useOnClickOutside(
    dropdownRef,
    () => {
      setIsDropdownOpen(false);
    },
    'mousedown'
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setQuery(inputValue);
      if (inputValue === '') {
        onChange && onChange('');
      }
      setIsDropdownOpen(true);
    },
    [onChange, setQuery]
  );

  const optionsListEl = useMemo(
    () =>
      isLoading ? (
        <SpinnerWrapper>
          <Spinner color="primary" size="sm" className="mx-auto" />
        </SpinnerWrapper>
      ) : filteredOptions?.length ? (
        <DropdownStyled as="ul" ref={dropdownRef} fullwidth={fullwidth} twStyle={listStyle}>
          {filteredOptions?.length ? (
            filteredOptions?.map((v: string) => (
              <BaseCombobox.Option
                key={v}
                value={v}
                onClick={() => setIsDropdownOpen(false)}
                as="li"
                className="rounded-lg px-0 py-0 lg:px-0 lg:py-0 relative h-12 flex items-center"
              >
                {({ active }) => <StyledComboboxOption active={active}>{v}</StyledComboboxOption>}
              </BaseCombobox.Option>
            ))
          ) : (
            <BaseCombobox.Option value="" onClick={() => setIsDropdownOpen(false)}>
              {noOptionsMessage || `-- ${translateCommon('no_options_loaded')} --`}
            </BaseCombobox.Option>
          )}
        </DropdownStyled>
      ) : query ? (
        <DropdownStyled as="ul" ref={dropdownRef} fullwidth={fullwidth} twStyle={listStyle}>
          <BaseCombobox.Option value="" onClick={() => setIsDropdownOpen(false)}>
            {noOptionsMessage || `-- ${translateCommon('no_options_loaded')} --`}
          </BaseCombobox.Option>
        </DropdownStyled>
      ) : null,
    [filteredOptions, fullwidth, isLoading, listStyle, noOptionsMessage, query, translateCommon]
  );

  useEffect(() => {
    onQueryChange && onQueryChange(query);
  }, [onQueryChange, query]);

  return (
    <SelectMainWrapper fullwidth={fullwidth}>
      <BaseCombobox
        value={value}
        onChange={(option: string | null) => {
          const value = option || '';
          onChange && onChange(value);
          setIsDropdownOpen(false);
        }}
        disabled={disabled}
      >
        {label && (
          <StyledComboboxLabel disabled={disabled}>
            <span>{label}</span> <StyledSecondaryLabel>{secondaryLabel}</StyledSecondaryLabel>
          </StyledComboboxLabel>
        )}
        <InputContainer fullwidth={fullwidth} twStyle={inputWrapperStyle}>
          <StyledInput
            onClick={() => setIsDropdownOpen(true)}
            onChange={handleChange}
            displayValue={(value: string) => value}
            placeholder={placeholder}
            isError={!!error}
            disabled={disabled}
            twStyle={inputStyle}
            autoComplete={autoCompleteOff ? 'off' : undefined}
          />
          {showIcon && (
            <ArrowIconWrapper isOpen={isDropdownOpen} className="absolute top-[13px] right-4">
              <ChevronDownIcon className="h-6 w-6 text-neutral-700" />
            </ArrowIconWrapper>
          )}
        </InputContainer>
        <InputContainer fullwidth={fullwidth} twStyle={inputWrapperStyle}>
          {isDropdownOpen && optionsListEl}
        </InputContainer>
      </BaseCombobox>
      {helperTextMessage && (
        <StyledSelectHelperText variant={helperTextVariant}>{helperTextMessage}</StyledSelectHelperText>
      )}
    </SelectMainWrapper>
  );
}
export default Combobox;

export interface ComboboxProps {
  value?: string;
  onChange?: (value: string) => void;
  dropdownList?: string[];
  placeholder?: string;
  inputWrapperStyle?: string;
  listStyle?: string;
  inputStyle?: string;
  showIcon?: boolean;
  noOptionsMessage?: ReactNode;
  error?: string;
  noFilter?: boolean;
  isLoading?: boolean;
  autoCompleteOff?: boolean;
  onQueryChange?: (query: string) => void;
  disabled?: boolean;
  helperText?: { variant?: HelperTextVariantType; message: string };
  label?: string;
  secondaryLabel?: string;
  fullwidth?: boolean;
}

const InputContainer = ({ fullwidth, twStyle, className, ...props }: any) => (
  <div className={cn('relative', fullwidth ? 'max-w-full' : 'max-w-[320px]', twStyle, className)} {...props} />
);
const DropdownStyled = ({ twStyle, fullwidth, className, ...props }: any) => (
  <BaseCombobox.Options
    className={cn(
      selectOptionsStyle,
      'py-2 text-gray-700 list-none',
      '[&_li]:relative [&_li]:px-4 [&_li]:py-2 [&_li]:hover:bg-gray-100 [&_li]:hover:cursor-pointer [&_li]:select-none [&_li]:text-left',
      fullwidth ? 'max-w-full' : 'max-w-[320px]',
      twStyle,
      className
    )}
    {...props}
  />
);
const StyledInput = ({ twStyle, isError, className, ...props }: any) => (
  <BaseCombobox.Input
    className={cn(
      selectInteractiveAreaStyle,
      selectInteractiveAreaContentStyle,
      'truncate pr-12 placeholder:text-base placeholder:font-normal placeholder:text-neutral-200 active:border-neutral-100 active:outline-none active:ring-0 focus:border-neutral-100 focus:outline-none focus:ring-0',
      props.disabled && 'bg-neutral-40 hover:bg-neutral-40 cursor-not-allowed',
      isError && 'border-none outline-red-200 bg-red-50',
      twStyle,
      className
    )}
    {...props}
  />
);
const SpinnerWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full flex items-center justify-center p-1', className)} {...props} />
);
const StyledComboboxLabel = ({ disabled, className, ...props }: any) => (
  <BaseCombobox.Label className={cn(selectLabelStyle, disabled && 'text-neutral-100', className)} {...props} />
);
const StyledSecondaryLabel = ({ className, ...props }: any) => (
  <span className={cn(selectSecondaryLabelStyle, className)} {...props} />
);
const StyledComboboxOption = ({ active, className, ...props }: any) => (
  <span
    className={cn(
      'w-full text-neutral-700 text-base font-medium truncate',
      active
        ? 'bg-neutral-30 rounded-lg h-full w-full inline-block absolute top-0 left-0 right-0 bottom-0 flex items-center px-4'
        : '',
      className
    )}
    {...props}
  />
);
